import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom'

// import LogoHeader from './LogoHeader';
import { parsePhoneNumberFromString, isValidPhoneNumber } from 'libphonenumber-js/max';
import { userLogin } from 'thunks/login/actions';
import { fetchMeData } from 'thunks/me/actions';
import { useDispatch } from 'react-redux';
import { getSavedRedirectParams, clearRedirectParams, buildRedirectUrl } from '../utils/urlUtils';

const Login = ({ error: propError }) => {
  const [phoneNumber, setMobileNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [pin, setPin] = useState(['', '', '', '']);
  const [loginMethod, setLoginMethod] = useState('pin');
  const [isSignup, setIsSignup] = useState(false);
  const [otp, setOtp] = useState('');

  const dispatch = useDispatch()
  const navigate = useNavigate()
  
  // Add formData state to track form data
  const [formData, setFormData] = useState({
    phoneNumber: 0,
    pin: 0,
    otp: 0
  });
    
  // Validate Indian phone number
  const validateIndianPhoneNumber = (number) => {
    try {
      const phoneNumberWithCountry = number.startsWith('+91') ? number : `+91${number}`;
      const phoneObj = parsePhoneNumberFromString(phoneNumberWithCountry, 'IN');

      if (!phoneObj) {
        return { isValid: false, error: 'Invalid phone number format' };
      }

      // Check if it's a valid Indian number
      if (!phoneObj.isValid()) {
        return { isValid: false, error: 'Please enter a valid phone number' };
      }

      // Check if it's a mobile number (India mobile numbers start with 6, 7, 8, or 9)
      const nationalNumber = phoneObj.nationalNumber;
      if (!/^[6-9]\d{9}$/.test(nationalNumber)) {
        return { isValid: false, error: 'Please enter a valid Indian mobile number' };
      }

      return { 
        isValid: true, 
        formattedNumber: phoneObj.format('NATIONAL'),
        e164Format: phoneObj.format('E.164')
      };
    } catch (error) {
      return { isValid: false, error: 'Invalid phone number' };
    }
  };

  // Handle phone number input
  const handlePhoneNumberInput = (e) => {
    const input = e.target.value.replace(/\D/g, '').slice(0, 10);
    setMobileNumber(input);
    
    // Update formData with new phone number as number type
    setFormData(prev => ({
      ...prev,
      phoneNumber: input ? parseInt(input, 10) : 0
    }));
    
    if (input.length === 0) {
      setPhoneError('Phone number is required');
      return;
    }

    if (input.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      return;
    }

    const validation = validateIndianPhoneNumber(input);
    setPhoneError(validation.isValid ? '' : validation.error);
  };

  // Handle PIN input with auto-focus
  const handlePinInput = (index, e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 1);
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Update formData with new PIN as number type
    const pinNumber = parseInt(newPin.join(''), 10);
    setFormData(prev => ({
      ...prev,
      pin: isNaN(pinNumber) ? 0 : pinNumber
    }));

    // If a number is entered, move to next input
    if (value && index < 3) {
      const nextInput = e.target.parentElement.querySelector(`input[data-index="${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle OTP input
  const handleOtpInput = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
    
    // Update formData with new OTP as number type
    setFormData(prev => ({
      ...prev,
      otp: value ? parseInt(value, 10) : 0
    }));
  };

  // Handle backspace in PIN input
  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const prevInput = e.target.parentElement.querySelector(`input[data-index="${index - 1}"]`);
      if (prevInput) {
        prevInput.focus();
        e.preventDefault();
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateIndianPhoneNumber(phoneNumber);
    if (!validation.isValid) {
      setPhoneError(validation.error);
      return;
    }

    // Prepare login data based on login method
    const loginPayload = {
      phoneNumber: formData.phoneNumber,
      ...(loginMethod === 'pin' 
        ? { pin: formData.pin } 
        : { otp: formData.otp }
      )
    };
    console.log(loginPayload, "loginPayload")
    dispatch(userLogin(loginPayload, postLoginAction))
  };

  const postLoginAction = (res, err) => {
    if (!err) {
      // Fetch user data after successful login
      dispatch(fetchMeData((userData, userError) => {
        if (!userError) {
          console.log('User data loaded:', userData);
        } else {
          console.error('Error loading user data:', userError);
        }
      }));

      // Check for saved redirect parameters
      const savedParams = getSavedRedirectParams();
      console.log('Login Success - Saved redirect params:', savedParams);
      console.log('Login Success - Current URL:', window.location.href);
      
      // Test: Check all localStorage items
      console.log('Login Success - All localStorage items:');
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        console.log(`${key}:`, localStorage.getItem(key));
      }
      
      if (savedParams && savedParams.test) {
        try {
          // Build the redirect URL with the test parameter
          const redirectUrl = `/start?test=${encodeURIComponent(savedParams.test)}`;
          console.log('Login Success - Redirecting to:', redirectUrl);
          
          // Clear saved params after building the URL
          clearRedirectParams();
          
          // Use setTimeout to ensure navigation happens after state updates
          setTimeout(() => {
            console.log('Login Success - Executing navigation to:', redirectUrl);
            navigate(redirectUrl, { replace: true });
          }, 100);
        } catch (error) {
          console.error('Error building redirect URL:', error);
          navigate("/start");
        }
      } else {
        console.log('Login Success - No saved params, going to /start');
        navigate("/start");
      }
      // dispatch(fireSuccessToaster('login successful'))
    } else {
      console.log('Login Error:', err);
      // dispatch(fireErrorToaster(res))
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* <LogoHeader /> */}
      
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="card p-8">
            <div className="text-center mb-8">
              <div className="mb-6">
                <div className="w-20 h-20 bg-white-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <img src="https://theexamhub.com/assets/images/logolatest.png" alt="TheExamHub" className="w-20 h-20" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  TheExamHub
                </h1>
                <p className="text-gray-600">
                  {isSignup ? 'Create your account' : 'Sign in to continue'}
                </p>
              </div>
            </div>

            {propError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-red-800">
                    {propError}
                  </span>
                </div>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-sm">+91</span>
                  </div>
                  <input
                    type="text"
                    id="phoneNumber"
                    value={phoneNumber}
                    onChange={handlePhoneNumberInput}
                    inputMode="numeric"
                    placeholder="Enter your phone number"
                    className={`input-field pl-12 ${phoneError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'}`}
                    autoComplete="tel-national"
                    required
                  />
                </div>
                {phoneError && (
                  <p className="mt-1 text-sm text-red-600">
                    {phoneError}
                  </p>
                )}
              </div>

              {loginMethod === 'pin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    4-Digit PIN
                  </label>
                  <div className="flex space-x-2">
                    {pin.map((digit, index) => (
                      <input
                        key={index}
                        data-index={index}
                        type="password"
                        value={digit}
                        onChange={(e) => handlePinInput(index, e)}
                        onKeyDown={(e) => handlePinKeyDown(index, e)}
                        className="w-12 h-12 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        maxLength={1}
                        inputMode="numeric"
                        autoComplete="off"
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your 4-digit security PIN
                  </p>
                  
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      onClick={() => setLoginMethod('otp')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium underline"
                    >
                      Login with OTP
                    </button>
                  </div>
                </div>
              )}

              {loginMethod === 'otp' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    6-Digit OTP
                  </label>
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={otp}
                      onChange={handleOtpInput}
                      placeholder="Enter 6-digit OTP"
                      className="input-field text-center text-lg tracking-widest"
                      maxLength={6}
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                    <button
                      type="button"
                      className="w-full py-2 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      Send OTP
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    We'll send you a verification code
                  </p>
                  
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      onClick={() => setLoginMethod('pin')}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium underline"
                    >
                      Login with PIN
                    </button>
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="btn-primary w-full"
              >
                {loginMethod === 'pin' ? 'Login' : 'Verify OTP'}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {isSignup ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  onClick={() => setIsSignup(!isSignup)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {isSignup ? 'Sign in' : 'Sign up'}
                </button>
              </p>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-center">
                <p className="text-xs text-gray-500 mb-2">
                  By continuing, you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Login.propTypes = {
  error: PropTypes.string
};

export default Login; 